/*

=========================================================
* Now UI Kit React - v1.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/now-ui-kit-react
* Copyright 2020 Creative Tim (http://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/now-ui-kit-react/blob/master/LICENSE.md)

* Designed by www.invisionapp.com Coded by www.creative-tim.com

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { Component, Suspense, lazy } from "react";
import { Router, Route, Switch, Redirect } from "react-router-dom";
import { getUser } from "utils";
import history from "utils/history";

// styles for this kit
import "index.css";
import "assets/css/tokens.css";
import "assets/css/bootstrap.min.css";
import "assets/scss/now-ui-kit.scss?v=1.4.0";
import "assets/demo/demo.css?v=1.4.0";
import "assets/demo/nucleo-icons-page-styles.css?v=1.4.0";
// pages for this kit
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/ProtectedRoute";
import Loading from "components/Common/Loading";

// Eager — public pages, first paint critical
import LoginPage from "views/examples/LoginPage";
import LandingPage from "views/examples/LandingPage";
import ProfilePage from "views/examples/ProfilePage";
import NucleoIcons from "views/NucleoIcons";

// Lazy — admin pages, loaded on demand
const DashboardPage = lazy(() => import("views/dashboard/DashboardPage"));
const ProductTypePage = lazy(() => import("views/productType/ProductTypePage"));
const ProductModelPage = lazy(() => import("views/productModel/ProductModelPage"));
const ProductPage = lazy(() => import("views/product/ProductPage"));
const BarcodePage = lazy(() => import("views/product/BarcodePage"));
const CustomerPage = lazy(() => import("views/customer/CustomerPage"));
const SettingPage = lazy(() => import("views/setting/SettingPage"));
const ShopeeItemPage = lazy(() => import("views/shopeeItem/ShopeeItemPage"));
const ShopeeOrderPage = lazy(() => import("views/shopeeOrder/ShopeeOrderPage"));
const TikTokItemPage = lazy(() => import("views/tiktokItem/TikTokItemPage"));
const InoutPage = lazy(() => import("views/inout/InoutPage"));
const ProductInPage = lazy(() => import("views/inout/ProductInPage"));
const ProductOutPage = lazy(() => import("views/inout/ProductOutPage"));
const ProductInCreatePage = lazy(() => import("views/inout/ProductInCreatePage"));
const ProductOutCreatePage = lazy(() => import("views/inout/ProductOutCreatePage"));
const RecapPage = lazy(() => import("views/recap/RecapPage"));
const PurchaseOrderPage = lazy(() => import("views/purchaseOrder/PurchaseOrderPage"));
const StockOpnameCheckPage = lazy(() => import("views/stockOpname/StockOpnameCheckPage"));

const Index = lazy(() => import("views/Index"));

class App extends Component {
  render() {
    const account = getUser();

    return (
      <Router history={history}>
        <ErrorBoundary>
          <Suspense
            fallback={
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100vh",
                }}
              >
                <Loading />
              </div>
            }
          >
            <ScrollToTop />
            <Switch>
            <Route path="/index" render={(props) => <Index {...props} />} />
            <Route
              exact
              path="/nucleo-icons"
              render={(props) => <NucleoIcons {...props} />}
            />
            <Route
              exact
              path="/landing-page"
              render={(props) => <LandingPage {...props} />}
            />
            <Route
              exact
              path="/profile-page"
              render={(props) => <ProfilePage {...props} />}
            />
            <Route
              exact
              path="/login"
              render={(props) => <LoginPage {...props} />}
            />
            <ProtectedRoute
              exact
              path="/a/dashboard"
              render={(props) => <DashboardPage {...props} />}
            />
            <ProtectedRoute
              exact
              path="/a/product-type"
              render={(props) => <ProductTypePage {...props} />}
            />
            <ProtectedRoute
              exact
              path="/a/product-model"
              render={(props) => <ProductModelPage {...props} />}
            />
            <ProtectedRoute
              exact
              path="/a/product"
              render={(props) => <ProductPage {...props} />}
            />
            <ProtectedRoute
              exact
              path="/a/recap"
              render={(props) => <RecapPage {...props} />}
            />
            <ProtectedRoute
              exact
              path="/a/purchase-order"
              render={(props) => <PurchaseOrderPage {...props} />}
            />
            <ProtectedRoute
              exact
              path="/a/customer"
              render={(props) => <CustomerPage {...props} />}
            />
            <ProtectedRoute
              exact
              path="/a/setting"
              render={(props) => <SettingPage {...props} />}
            />
            <ProtectedRoute
              exact
              path="/a/shopee/item"
              render={(props) => <ShopeeItemPage {...props} />}
            />
            <ProtectedRoute
              exact
              path="/a/shopee/order"
              render={(props) => <ShopeeOrderPage {...props} />}
            />
            <ProtectedRoute
              exact
              path="/a/tiktok/item"
              render={(props) => <TikTokItemPage {...props} />}
            />
            <ProtectedRoute
              exact
              path="/a/inout"
              render={(props) => <InoutPage {...props} />}
            />
            <ProtectedRoute
              exact
              path="/a/product/entered"
              render={(props) => <ProductInPage {...props} />}
            />
            <ProtectedRoute
              exact
              path="/a/product/entered/create"
              render={(props) => <ProductInCreatePage {...props} />}
            />
            <ProtectedRoute
              exact
              path="/a/product/out"
              render={(props) => <ProductOutPage {...props} />}
            />
            <ProtectedRoute
              exact
              path="/a/product/out/create"
              render={(props) => <ProductOutCreatePage {...props} />}
            />
            <ProtectedRoute
              exact
              path="/a/stock-opname/check"
              render={(props) => <StockOpnameCheckPage {...props} />}
            />
            <ProtectedRoute
              exact
              path="/a/barcode/:id"
              render={(props) => <BarcodePage {...props} />}
            />
            <Redirect to="/index" />
            <Redirect from="/" to="/index" />
          </Switch>
        </Suspense>
        </ErrorBoundary>
      </Router>
    );
  }
}

export default App;
